<template>
  <div class="cart">
    <div class="cart__block" v-if="productsInCart.length">
      <h6 class="cart__title">{{ t('profile.cart.title') }}</h6>
      <div class="cart__block-inner">
        <div class="table">
          <div class="table__top">
            <div class="table__column"></div>
            <div class="table__column">
              <p class="table__title">{{ t('profile.cart.product') }}</p>
            </div>
            <div class="table__column">
              <p class="table__title">{{ t('profile.cart.price') }}</p>
            </div>
            <div class="table__column">
              <p class="table__title">{{ t('profile.cart.quantity') }}</p>
            </div>
            <div class="table__column">
              <p class="table__title">{{ t('profile.cart.subtotal') }}</p>
            </div>
          </div>
          <product-card-in-cart
              v-for="product in productsInCart"
              :key="product.node.product.uuid"
              :product="product.node"
          />
        </div>
        <div class="cart__checkout">
          <p>{{ t('profile.cart.cartTotals') }}</p>
          <div>
            <p>{{ t('profile.cart.total') }}</p>
            <p>€{{ totalPrice }}</p>
          </div>
          <ui-checkbox
              v-model="isAgree"
          >
            <i18n-t tag="p" scope="global" keypath="checkboxes.agree">
              <template #terms>
                <router-link
                    class="form__link"
                    :to="translations.i18nRoute({ name: 'terms' })"
                >
                  {{ t('docs.terms.title') }}
                </router-link>
              </template>
              <template #policy>
                <router-link
                    class="form__link"
                    :to="translations.i18nRoute({ name: 'policy' })"
                >
                  {{ t('docs.policy.title') }}
                </router-link>
              </template>
            </i18n-t>
          </ui-checkbox>
          <ui-button
              class="cart__checkout-button"
              :isDisabled="!isAgree"
              @click="buyOrder"
          >
            {{ t('buttons.checkout') }}
          </ui-button>
        </div>
      </div>
    </div>
    <div class="cart__empty" v-else>
      <svg width="224" viewBox="0 0 77 77" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M54 52L20.02 52.3603C19.558 52.3603 19.096 52.0523 18.788 51.7443C18.48 51.4363 18.48 50.8203 18.634 50.3583L22.5 42.9663L17.5 13H7.5V11H18.5C19.27 11 19.346 11.73 19.5 12.5L24.64 42.9663C24.64 43.2743 24.64 43.4283 24.486 43.7363L21 50.3583L55 50L54 52Z" fill="#F1F1F1"/>
        <path d="M22.7921 44.9995V42.9995H56.5001L60 25.4995L20.5 20.9995V18.8311L60.5 23.4995C61.424 23.4995 62 24.5755 62 25.4995L60 35.4995C60 35.4995 58.77 44.8455 58 44.9995H22.7921ZM55.4401 60.9995C52.0521 60.9995 50 58.828 50 55.44C50 52.052 52.0521 49.9995 55.4401 49.9995C58.8281 49.9995 61 52.1115 61 55.4995C61 58.8875 58.8281 60.9995 55.4401 60.9995ZM55.4401 52.36C53.7461 52.36 52.3601 53.746 52.3601 55.44C52.3601 57.134 53.7461 58.52 55.4401 58.52C57.1341 58.52 58.5201 57.134 58.5201 55.44C58.5201 53.746 57.1341 52.36 55.4401 52.36ZM18.4801 60.9995C15.0921 60.9995 13 58.8875 13 55.4995C13 52.1115 15.0921 49.9995 18.4801 49.9995C21.8681 49.9995 24 52.052 24 55.44C24 58.828 21.8681 60.9995 18.4801 60.9995ZM18.4801 52.36C16.7861 52.36 15.4001 53.746 15.4001 55.44C15.4001 57.134 16.7861 58.52 18.4801 58.52C20.1741 58.52 21.5601 57.134 21.5601 55.44C21.5601 53.746 20.1741 52.36 18.4801 52.36Z" fill="#F1F1F1"/>
      </svg>
      <h6 class="cart__empty-title">{{ t('profile.cart.empty.title') }}</h6>
      <p class="cart__empty-text">{{ t('profile.cart.empty.text') }}</p>
      <router-link :to="translations.i18nRoute({ name: 'store' })">
        <ui-button class="cart__empty-button">{{ t('buttons.returnToShop') }}</ui-button>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import UiButton from "@/components/ui/ui-button.vue";
import {useI18n} from "vue-i18n";
import {computed, ref} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import ProductCardInCart from "@/components/cards/product-card-in-cart.vue";
import {ElNotification} from "element-plus";
import {Orders} from "@/core/api/orders.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import translations from "@/core/helpers/translations.js";

const {t} = useI18n()
const cartStore = useCartStore()
const authStore = useAuthStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const userEmail = computed(() => {
  return authStore.user ? authStore.user.email : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})

const totalPrice = computed(() => {
  return cartStore.currentOrder?.totalPrice ? cartStore.currentOrder?.totalPrice : 0
})
const productsInCart = computed(() => {
  return cartStore.currentOrder.orderProducts ? cartStore.currentOrder.orderProducts.edges : []
})
const isAgree = ref(false)

const buyOrder = async () => {
  const res = await Orders.buyOrder(accessToken.value, orderUuid.value)

  if (res.errors) {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  } else {
    if (res?.data?.buyOrder?.transaction?.process?.url) {
      const currentOrder = await Orders.getOrders("PENDING", accessToken.value, userEmail.value)
      cartStore.setCurrentOrders(currentOrder.data.orders.edges[0].node)

      window.location.href = res.data.buyOrder.transaction.process.url
    } else {
      console.log(res.data)
    }
  }
}
</script>

<style lang="scss" scoped>
.cart {
  &__title {
    color: #2F2F2F;
    font-size: 18px;
    font-weight: 600;
    line-height: 30.8px;
    text-transform: uppercase;
  }

  &__block {
    &-inner {
      border-top: 1px solid $black;
      padding-top: 20px;
      display: flex;
      align-items: flex-start;
      gap: 40px;
    }
  }

  &__checkout {
    width: 256px;
    display: flex;
    flex-direction: column;
    gap: 25px;
    padding: 25px 30px;
    border-radius: 10px;
    border: 3px solid #ECECEC;

    & p {
      color: #2F2F2F;
      font-size: 20px;
      font-weight: 600;
      line-height: 30.8px;
    }

    & div {
      display: flex;
      align-items: center;
      justify-content: space-between;

      & p {
        font-size: 18px;
      }
    }

    &-button {
      flex-shrink: 0;
      white-space: nowrap;
      width: fit-content;
      padding: 15px 10px;
      font-size: 16px;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 18px;

    &-title {
      color: #2F2F2F;
      text-align: center;
      font-size: 42px;
      font-weight: 600;
      line-height: 57.6px;
    }

    &-text {
      color: #5A5A5A;
      text-align: center;
      font-size: 16px;
      font-weight: 400;
      line-height: 28.16px;
    }

    &-button {
      width: fit-content;
      padding-block: 15px;
      font-size: 14px;
    }
  }
}

.table {
  width: 100%;

  &__top {
    display: flex;
    align-items: center;
    border-bottom: 2px solid #E4E4E4;
    padding: 0 10px 10px 10px;
  }

  &__title {
    color: #2F2F2F;
    font-size: 16px;
    font-weight: 600;
    line-height: 30.8px;
    text-transform: uppercase;
  }

  &__column {
    &:nth-child(1) {
      width: 10%;
    }
    &:nth-child(2) {
      width: 30%;
    }
    &:nth-child(3) {
      width: 15%;
    }
    &:nth-child(4) {
      width: 30%;
      display: flex;
      justify-content: center;
    }
    &:nth-child(5) {
      width: 15%;
      display: flex;
      justify-content: flex-end;
    }
  }
}

@media (max-width: 1200px) {
  .cart {
    &__block-inner {
      flex-direction: column;
      gap: 20px;
    }
  }
}

@media (max-width: 850px) {
  .cart {
    &__checkout {
      width: fit-content;
    }
  }

  .table {
    overflow-x: auto;
    max-width: 70%;

    &__top {
      width: 750px;
    }

    &__item {
      width: 750px;
    }
  }
}

@media (max-width: 750px) {
  .cart {
    &__empty {
      gap: 10px;

      & svg {
        width: 150px;
      }

      &-title {
        font-size: 26px;
      }

      &-text {
        font-size: 12px;
        line-height: 16px;
      }

      &-button {
        padding-block: 10px;
      }
    }
  }
}

@media (max-width: 450px) {
  .cart {
    &__checkout {
      padding: 15px 20px;
    }
  }
}
</style>